import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'

interface UserProfile {
  id: string
  email?: string
  name?: string
  facility_id?: string
  facility_role?: string
  subscription_status?: string
  active_plan?: string
  university_access?: boolean
  global_academic_profile?: any
  prep_pathway_mode?: string
  prep_pathway_set_at?: string
  prep_pathway_set_by?: string
  scholar_pass_id?: string
  nfp_pass_id?: string
  is_affiliate?: boolean
  affiliate_status?: string
  affiliate_code_used?: string
  affiliate_discount_applied?: boolean
  linked_facilities?: any[]
}

interface AuthContextValue {
  user: User | null
  session: Session | null
  userProfile: UserProfile | null
  isLoading: boolean
  signIn: (email: string, password: string) => Promise<{ data: any, error: Error | null }>
  signUp: (email: string, password: string, meta?: Record<string, string>) => Promise<{ data: any, error: Error | null }>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  switchWorkspace: (facilityId: string, role: string) => Promise<{ error: Error | null }>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // ── Load profile from the CORRECT table: users (not profiles) ───────────────
  const loadProfile = useCallback(async (uid: string) => {
    try {
      const { data } = await supabase
        .from('users')
        .select('id, email, name, facility_id, facility_role, subscription_status, active_plan, university_access, global_academic_profile, prep_pathway_mode, prep_pathway_set_at, prep_pathway_set_by, scholar_pass_id, nfp_pass_id, is_affiliate, affiliate_status, affiliate_code_used, affiliate_discount_applied, user_facilities(*, facilities(name, type))')
        .eq('id', uid)
        .single()
      if (data) {
        // If profile exists but no code is linked, check localStorage (covers OAuth signup)
        if (!data.affiliate_code_used) {
          const storedRef = typeof window !== 'undefined' ? localStorage.getItem('sage_affiliate_ref') : null;
          if (storedRef) {
            await supabase.from('users').update({
              affiliate_code_used: storedRef,
              affiliate_discount_applied: true
            }).eq('id', uid);
            data.affiliate_code_used = storedRef;
            data.affiliate_discount_applied = true;
          }
        }
        const profile = data as any;
        profile.linked_facilities = profile.user_facilities || [];
        setUserProfile(profile as unknown as UserProfile)
      }
    } catch {
      // First login — user row may not exist yet (handle_new_user trigger creates it)
      setUserProfile(null)
    }
  }, [supabase])

  const refreshProfile = useCallback(async () => {
    if (user?.id) await loadProfile(user.id)
  }, [user, loadProfile])

  useEffect(() => {
    let mounted = true

    // 1. Restore existing session on mount (also handles OAuth code exchange via detectSessionInUrl)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return
      console.log('[AUTH] Initial session detection:', {
        hasSession: !!session,
        userId: session?.user?.id,
        expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
        tokenPrefix: session?.access_token ? `${session.access_token.substring(0, 10)}...` : 'none'
      })
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) loadProfile(session.user.id)
      setIsLoading(false)
    })

    // 2. Listen for auth state changes (sign in, sign out, OAuth callback, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return
      console.log(`[AUTH] Event: ${event}`, {
        userId: session?.user?.id,
        tokenPrefix: session?.access_token ? `${session.access_token.substring(0, 10)}...` : 'none'
      })
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        loadProfile(session.user.id)
      } else {
        setUserProfile(null)
      }
      setIsLoading(false)
    })

    return () => { mounted = false; subscription.unsubscribe() }
  }, [loadProfile])

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    return { data, error: error as Error | null }
  }, [supabase])

  const signUp = useCallback(async (email: string, password: string, meta?: Record<string, string>) => {
    const affiliateCode = typeof window !== 'undefined' ? localStorage.getItem('sage_affiliate_ref') : null;
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: {
        data: {
          ...meta,
          affiliate_code_used: affiliateCode || undefined,
          affiliate_discount_applied: affiliateCode ? true : false,
        },
        emailRedirectTo: `${window.location.origin}/auth/verify`
      }
    })
    return { data, error: error as Error | null }
  }, [supabase])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    setUser(null)
    setSession(null)
    setUserProfile(null)
  }, [])

  const switchWorkspace = useCallback(async (facilityId: string, role: string) => {
    if (!user) return { error: new Error('No user logged in') }

    const { error } = await supabase
      .from('users')
      .update({
        facility_id: facilityId,
        facility_role: role,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id)

    if (!error) {
      await refreshProfile()
    }

    return { error: error as Error | null }
  }, [user, supabase, refreshProfile])

  return (
    <AuthContext.Provider value={{ user, session, userProfile, isLoading, signIn, signUp, signOut, refreshProfile, switchWorkspace }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuthContext must be inside AuthProvider')
  return ctx
}